'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type FocusEvent,
  type MouseEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { Slot } from 'radix-ui';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type TooltipIconButtonProps = ComponentPropsWithoutRef<typeof Button> & {
  tooltip: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  children?: ReactNode;
};

type Coords = { top: number; left: number };

function placementStyle(
  side: 'top' | 'bottom' | 'left' | 'right',
  rect: DOMRect
): Coords & { transform: string } {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  switch (side) {
    case 'bottom':
      return { top: rect.bottom + 8, left: cx, transform: 'translate(-50%, 0)' };
    case 'left':
      return {
        top: cy,
        left: rect.left - 8,
        transform: 'translate(-100%, -50%)'
      };
    case 'right':
      return { top: cy, left: rect.right + 8, transform: 'translate(0, -50%)' };
    case 'top':
    default:
      return {
        top: rect.top - 8,
        left: cx,
        transform: 'translate(-50%, -100%)'
      };
  }
}

export const TooltipIconButton = forwardRef<
  HTMLButtonElement,
  TooltipIconButtonProps
>(
  (
    {
      children,
      tooltip,
      side = 'top',
      className,
      variant = 'ghost',
      size = 'icon-xs',
      type = 'button',
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const tipId = useId();
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState<CSSProperties>({});

    const setButtonRef = useCallback(
      (node: HTMLButtonElement | null) => {
        btnRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const showTip = useCallback(() => {
      const el = btnRef.current;
      if (!el) return;
      const next = placementStyle(side, el.getBoundingClientRect());
      const dark =
        document.documentElement.getAttribute('data-bs-theme') === 'dark';
      setStyle({
        position: 'fixed',
        top: next.top,
        left: next.left,
        transform: next.transform,
        zIndex: 2000,
        pointerEvents: 'none',
        maxWidth: '16rem',
        padding: '0.3rem 0.55rem',
        borderRadius: '0.45rem',
        background: dark ? '#e8edf5' : '#0d0d0d',
        color: dark ? '#0b1727' : '#fff',
        fontSize: '0.7rem',
        fontWeight: 600,
        lineHeight: 1.35,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        boxShadow: dark
          ? '0 8px 20px rgba(0, 0, 0, 0.35)'
          : '0 8px 20px rgba(15, 23, 42, 0.2)'
      });
      setOpen(true);
    }, [side]);

    const hideTip = useCallback(() => setOpen(false), []);

    useEffect(() => {
      if (!open) return;
      const reposition = () => showTip();
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
      return () => {
        window.removeEventListener('scroll', reposition, true);
        window.removeEventListener('resize', reposition);
      };
    }, [open, showTip]);

    const handleMouseEnter = (e: MouseEvent<HTMLButtonElement>) => {
      onMouseEnter?.(e);
      showTip();
    };
    const handleMouseLeave = (e: MouseEvent<HTMLButtonElement>) => {
      onMouseLeave?.(e);
      hideTip();
    };
    const handleFocus = (e: FocusEvent<HTMLButtonElement>) => {
      onFocus?.(e);
      showTip();
    };
    const handleBlur = (e: FocusEvent<HTMLButtonElement>) => {
      onBlur?.(e);
      hideTip();
    };

    return (
      <>
        <Button
          asChild
          variant={variant}
          size={size}
          className={cn(
            'aui-button-icon size-6 p-0 active:scale-95',
            className
          )}
        >
          <button
            type={type}
            {...rest}
            ref={setButtonRef}
            aria-label={tooltip}
            aria-describedby={open ? tipId : undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <Slot.Slottable>{children}</Slot.Slottable>
            <span className="aui-sr-only sr-only">{tooltip}</span>
          </button>
        </Button>
        {open && typeof document !== 'undefined'
          ? createPortal(
              <div id={tipId} role="tooltip" dir="rtl" style={style}>
                {tooltip}
              </div>,
              document.body
            )
          : null}
      </>
    );
  }
);

TooltipIconButton.displayName = 'TooltipIconButton';
